/**
 * SyncInProgressChip — small "we're still hydrating" pill rendered on a
 * connection row while a background sync / webhook reconciliation is in
 * flight.
 *
 * Use-case prima facie: post-OAuth callback for capabilities whose
 * provider hits us back ASYNC after the redirect — PSD2 open-banking
 * (1-2 min Acube webhook), Cassetto Fiscale (24-48h SPID delega
 * confirmation), Salesforce REST sandbox (warm-up retries). The naive
 * approach of toasting "Collegata" immediately on ``?status=connected``
 * is bugiardo — the row appears in the list AFTER the webhook lands.
 *
 * The chip lives on the row itself; the host hook (e.g.
 * ``usePollConnectionStatus``) decides when to mount / unmount it based
 * on the poll outcome.
 *
 * Design notes:
 *   - Mono Lucide ``Loader2`` (already used elsewhere) — no third-party
 *     spinner dep.
 *   - ``prefers-reduced-motion`` collapses the spin to a static dot via
 *     the global Tailwind ``motion-reduce:animate-none``.
 *   - A11y: ``role="status"`` + ``aria-live="polite"`` so screen readers
 *     announce the transition; ``aria-label`` carries the textual hint
 *     for AT users who don't see the spinner.
 *   - Visual language matches ``<HealthDot>`` + ``<SyncStatusPill>`` —
 *     consistent with the row chrome.
 */
import { Loader2 } from "lucide-react";
import type * as React from "react";

import { cn } from "../../lib/cn";

export interface SyncInProgressChipProps {
  /**
   * Optional short Italian copy override. Default: ``"Sincronizzazione…"``.
   * Pass something specific (``"In attesa AdE"``, ``"Apertura consenso"``)
   * when the capability flow has a more accurate hint.
   */
  readonly label?: string;
  /**
   * Optional ETA in seconds — surfaced as a "~Xs" tail (``"Sincronizzazione… ~30s"``).
   * Omit when there's no realistic estimate; lying to the user is worse
   * than silence.
   */
  readonly etaSeconds?: number;
  readonly className?: string;
}

export const SyncInProgressChip: React.FC<SyncInProgressChipProps> = ({
  label = "Sincronizzazione…",
  etaSeconds,
  className,
}) => {
  const text =
    typeof etaSeconds === "number" && etaSeconds > 0
      ? `${label} ~${etaSeconds}s`
      : label;
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={text}
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-accent-9/30 bg-accent-2/40 px-2 font-medium text-[11px] text-accent-11",
        className,
      )}
    >
      <Loader2
        className="h-3 w-3 animate-spin motion-reduce:animate-none"
        aria-hidden
      />
      {text}
    </span>
  );
};

SyncInProgressChip.displayName = "SyncInProgressChip";
